<template>
  <div v-if="$userInfo">
    <app-detail-header :show-toggle="true"
      :show-delete="false"
      :show-save="false"
      :show-cancel="false"
      :show-print="false"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <country-setting-list-side-menu v-model="filter"
          @filter="filterList"
          @reset="resetList" />
      </div>
      <div class="column">
        <div class="tile is-parent">
          <article class="tile is-child box">
            <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
              :columns="columns"
              :page-index="filter.pageIndex"
              :page-size="filter.pageSize"
              :total-rows="totalRows"
              :is-loading="isTableLoading"
              :sort-column="filter.sortColumn"
              :sort-order="filter.sortOrder"
              @pageChanged="onPageChange"
              @sort="sort">
              <tr v-for="(entity, index) in entitySummaries"
                :key="entity.countryId"
                :class="{ 'is-selected' : selectedRow === index }"
                @click="highlightSelected(index, $event)">
                <td>
                  <a @click.prevent="edit(entity.countryId, $event)">
                    {{ entity.countryCode }}
                  </a>
                </td>
                <td>{{ entity.countryDesc }}</td>
                <td>{{ entity.nationality }}</td>
                <td>{{ entity.callingCode }}</td>
                <td class="has-text-right">{{ entity.gstRate }}</td>
                <td class="has-text-centered">
                  <span class="icon"
                    :class="[ entity.deleted ? 'has-text-danger' : 'has-text-success' ]">
                    <i class="mso mso-24px"> {{ entity.deleted ? 'close' : 'check' }} </i>
                  </span>
                </td>
                <td class="has-text-centered">
                  <a class="button is-primary is-small is-inverted tooltip"
                    @click.prevent="edit(entity.countryId, $event)"
                    data-tooltip="Click to edit">
                    <span class="icon is-medium">
                      <span class="mso mso-24px">
                        edit
                      </span>
                    </span>
                  </a>
                </td>
              </tr>
              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <span icon="icon is-large">
                      <i class="mdi mdi-48px mdi-emoticon-sad" />
                    </span>
                    <p>Nothing</p>
                  </div>
                </section>
              </template>
            </bulma-table>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppDetailHeader from '@/components/AppDetailHeader'
import { AppHeaderButtonTypes } from '@/enums'
import CountrySettingService from './CountrySettingService'
import BulmaTable from '@/components/BulmaTable'
import { Columns } from './columns'
import StoreMixin from './storeMixin'
import CountrySettingValidation from './CountrySettingValidation'
import CountrySettingListSideMenu from './CountrySettingListSideMenu'
import routeTypes from './route-types'

export default {
  name: 'CountrySettingListView',
  components: {
    AppDetailHeader,
    BulmaTable,
    [CountrySettingListSideMenu.name]: CountrySettingListSideMenu
  },
  mixins: [StoreMixin, CountrySettingValidation],
  data() {
    return {
      entitySummaries: [],
      totalRows: 0,
      filter: {
        query: '',
        active: 1,
        sortColumn: Columns[0].name,
        sortOrder: Columns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 18
      },
      selectedRow: null,
      isTableLoading: false
    }
  },
  computed: {
    filterKey() {
      if (this.$userInfo) {
        return `${this.$userInfo.sessionId}|${this.$route.meta.fkey}`
      } else {
        return ''
      }
    },
    columns() {
      return Columns
    }
  },
  created() {
    this.getData()
  },
  mounted() {
    if (this.$v && this.$v.filter) {
      this.$v.filter.$touch()
    }
  },
  methods: {
    persistFilter() {
      try {
        if (this.filterKey) sessionStorage.setItem(this.filterKey, JSON.stringify(this.filter))
      } catch (e) {
        // ignore
      }
    },
    retrieveFilter() {
      try {
        const stored = sessionStorage.getItem(this.filterKey)
        if (stored) this.filter = JSON.parse(stored)
      } catch (e) {
        // ignore
      }
    },
    filterList() {
      if (this.$v && this.$v.filter && this.$v.filter.$error) {
        this.$notification.openNotificationWithType('danger', 'Country Settings', 'Filter validation errors.')
        return false
      }
      this.filter.pageIndex = 1
      this.persistFilter()
      this.getData()
    },
    resetList() {
      this.filter.query = ''
      this.filter.active = 1
      this.filter.sortColumn = Columns[0].name
      this.filter.sortOrder = Columns[0].defaultOrder
      this.filter.pageIndex = 1
      this.filter.pageSize = 18
      this.persistFilter()
      this.getData()
    },
    highlightSelected(index) {
      this.selectedRow = index
    },
    edit(id) {
      this.$router.push({ name: routeTypes.CountrySettingDetail.name, params: { countryId: id } })
    },
    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.AddNew:
          this.addNew()
          break
      }
    },
    async addNew() {
      await this.addStoreItem()
      // this.$router.push({ name: routeTypes.CountrySettingDetail.name, query: { mode: 'new' } })
    },
    async getData() {
      this.isTableLoading = true
      try {
        const result = await CountrySettingService.getEntitySummaries(this.filter)
        this.entitySummaries = result || []
        this.totalRows = result.total || this.entitySummaries.length
      } catch (e) {
        this.$notification.openNotificationWithType('danger', 'Country Settings', e.message)
      }
      this.isTableLoading = false
    },
    onPageChange(pageIndex) {
      this.filter.pageIndex = pageIndex
      this.getData()
    },
    sort(name, order) {
      this.filter.sortColumn = name
      this.filter.sortOrder = order
      this.getData()
    }
  }
}
</script>

<style lang="scss" scoped>
.is-divider {
  margin: 0 0 1.2rem 0;
}
</style>
